import React, { useState, useMemo, useCallback } from 'react';
import {
    Card,
    Form,
    InputNumber,
    Switch,
    Button,
    Space,
    Typography,
    Row,
    Col,
    message,
    Spin,
    Alert,
} from 'antd';
import {
    ThunderboltOutlined,
    LockOutlined,
    SaveOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetSystemSettingsByCategoryQuery,
    useUpdateSystemSettingMutation,
} from '../../../services/nestjs-base-api';

const { Text } = Typography;

export const RateLimitingTab: React.FC = () => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        data: categorizedData,
        isLoading,
        refetch,
    } = useGetSystemSettingsByCategoryQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [updateSetting] = useUpdateSystemSettingMutation();

    // Extract all settings into a flat map for easy lookup
    const settingsMap = useMemo(() => {
        const map = new Map<string, any>();
        if (categorizedData?.categories) {
            categorizedData.categories.forEach((category) => {
                category.settings.forEach((setting) => {
                    map.set(setting.name, setting);
                });
            });
        }
        return map;
    }, [categorizedData]);

    // Helper to get setting value
    const getSetting = useCallback((name: string, defaultValue: any = '') => {
        const setting = settingsMap.get(name);
        return setting?.value ?? defaultValue;
    }, [settingsMap]);

    // Set form values when data is loaded
    React.useEffect(() => {
        if (!isLoading && settingsMap.size > 0) {
            form.setFieldsValue({
                enabled: getSetting('RATE_LIMIT_ENABLED', 'true') === 'true',
                globalLimit: parseInt(getSetting('RATE_LIMIT_GLOBAL_LIMIT', '100')),
                globalTtl: parseInt(getSetting('RATE_LIMIT_GLOBAL_TTL', '60')),
                loginLimit: parseInt(getSetting('RATE_LIMIT_LOGIN_LIMIT', '10')),
                loginTtl: parseInt(getSetting('RATE_LIMIT_LOGIN_TTL', '60')),
                failedLoginThreshold: parseInt(getSetting('RATE_LIMIT_FAILED_LOGIN_THRESHOLD', '5')),
                lockoutDuration: parseInt(getSetting('RATE_LIMIT_LOCKOUT_DURATION', '1800')),
            });
        }
    }, [isLoading, settingsMap, form, getSetting]);

    const handleSave = async (values: any) => {
        setIsSubmitting(true);
        try {
            const updates = [
                { name: 'RATE_LIMIT_ENABLED', value: values.enabled.toString() },
                { name: 'RATE_LIMIT_GLOBAL_LIMIT', value: values.globalLimit.toString() },
                { name: 'RATE_LIMIT_GLOBAL_TTL', value: values.globalTtl.toString() },
                { name: 'RATE_LIMIT_LOGIN_LIMIT', value: values.loginLimit.toString() },
                { name: 'RATE_LIMIT_LOGIN_TTL', value: values.loginTtl.toString() },
                { name: 'RATE_LIMIT_FAILED_LOGIN_THRESHOLD', value: values.failedLoginThreshold.toString() },
                { name: 'RATE_LIMIT_LOCKOUT_DURATION', value: values.lockoutDuration.toString() },
            ];

            await Promise.all(
                updates.map((setting) =>
                    updateSetting({
                        jwtToken: user?.jwtToken || '',
                        settingName: setting.name,
                        payload: { value: setting.value },
                    }).unwrap()
                )
            );

            message.success('Rate limiting settings saved successfully');
            refetch();
        } catch (error) {
            console.error('Failed to save rate limiting settings:', error);
            message.error('Failed to save settings');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            <Alert
                message="Rate Limiting & Security Configuration"
                description="Configure API rate limits and failed login protection to prevent abuse and protect the platform from brute force attacks. Changes take effect immediately across all API endpoints."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Form form={form} layout="vertical" onFinish={handleSave}>
                <Card
                    title={
                        <Space>
                            <ThunderboltOutlined style={{ marginRight: 4 }} />
                            Global Rate Limiting
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Form.Item name="enabled" label="Enable Rate Limiting" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Global rate limits apply to all API endpoints. Requests exceeding these limits will receive a 429 (Too Many Requests) response.
                    </Text>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="globalLimit"
                                label="Global Request Limit"
                                tooltip="Maximum number of requests allowed per time window"
                                rules={[{ required: true, message: 'Please enter a global limit' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={10000}
                                    style={{ width: '100%' }}
                                    addonAfter="requests"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="globalTtl"
                                label="Time Window (TTL)"
                                tooltip="Time window in seconds for the rate limit"
                                rules={[{ required: true, message: 'Please enter a TTL' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={3600}
                                    style={{ width: '100%' }}
                                    addonAfter="seconds"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        message={
                            <span>
                                <strong>Current setting:</strong> {form.getFieldValue('globalLimit') || 100} requests per {form.getFieldValue('globalTtl') || 60} seconds
                            </span>
                        }
                        type="success"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </Card>

                <Card
                    title={
                        <Space>
                            <LockOutlined style={{ marginRight: 4 }} />
                            Login Rate Limiting
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Specific rate limits for login endpoints to prevent credential stuffing attacks.
                    </Text>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="loginLimit"
                                label="Login Request Limit"
                                tooltip="Maximum number of login attempts allowed per time window"
                                rules={[{ required: true, message: 'Please enter a login limit' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={100}
                                    style={{ width: '100%' }}
                                    addonAfter="attempts"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="loginTtl"
                                label="Time Window (TTL)"
                                tooltip="Time window in seconds for login rate limit"
                                rules={[{ required: true, message: 'Please enter a TTL' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={3600}
                                    style={{ width: '100%' }}
                                    addonAfter="seconds"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        message={
                            <span>
                                <strong>Current setting:</strong> {form.getFieldValue('loginLimit') || 10} login attempts per {form.getFieldValue('loginTtl') || 60} seconds
                            </span>
                        }
                        type="success"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </Card>

                <Card
                    title={
                        <Space>
                            <WarningOutlined style={{ marginRight: 4 }} />
                            Failed Login Protection
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Alert
                        message="Brute Force Protection"
                        description="These settings protect user accounts from brute force attacks by temporarily locking accounts after too many failed login attempts."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="failedLoginThreshold"
                                label="Failed Login Threshold"
                                tooltip="Number of failed login attempts before account is locked"
                                rules={[{ required: true, message: 'Please enter a threshold' }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={20}
                                    style={{ width: '100%' }}
                                    addonAfter="attempts"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lockoutDuration"
                                label="Account Lockout Duration"
                                tooltip="How long the account remains locked after threshold is reached"
                                rules={[{ required: true, message: 'Please enter a duration' }]}
                            >
                                <InputNumber
                                    min={60}
                                    max={86400}
                                    style={{ width: '100%' }}
                                    addonAfter="seconds"
                                    formatter={value => {
                                        const num = Number(value);
                                        if (num >= 3600) return `${num} (${Math.floor(num / 3600)}h)`;
                                        if (num >= 60) return `${num} (${Math.floor(num / 60)}m)`;
                                        return `${num}`;
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        message={
                            <span>
                                <strong>Current setting:</strong> Lock account for {Math.floor((form.getFieldValue('lockoutDuration') || 1800) / 60)} minutes after {form.getFieldValue('failedLoginThreshold') || 5} failed login attempts
                            </span>
                        }
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </Card>

                <Card
                    title={
                        <Space>
                            <WarningOutlined style={{ marginRight: 4 }} />
                            Common Presets
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Quick presets for common rate limiting scenarios. Click to apply.
                    </Text>

                    <Space wrap>
                        <Button
                            onClick={() => {
                                form.setFieldsValue({
                                    enabled: true,
                                    globalLimit: 1000,
                                    globalTtl: 60,
                                    loginLimit: 20,
                                    loginTtl: 60,
                                    failedLoginThreshold: 10,
                                    lockoutDuration: 900,
                                });
                                message.info('Applied "Relaxed" preset');
                            }}
                        >
                            Relaxed (1000/min, 20 login/min)
                        </Button>
                        <Button
                            onClick={() => {
                                form.setFieldsValue({
                                    enabled: true,
                                    globalLimit: 100,
                                    globalTtl: 60,
                                    loginLimit: 10,
                                    loginTtl: 60,
                                    failedLoginThreshold: 5,
                                    lockoutDuration: 1800,
                                });
                                message.info('Applied "Balanced" preset (recommended)');
                            }}
                            type="primary"
                        >
                            Balanced - Recommended (100/min, 10 login/min)
                        </Button>
                        <Button
                            onClick={() => {
                                form.setFieldsValue({
                                    enabled: true,
                                    globalLimit: 50,
                                    globalTtl: 60,
                                    loginLimit: 5,
                                    loginTtl: 60,
                                    failedLoginThreshold: 3,
                                    lockoutDuration: 3600,
                                });
                                message.info('Applied "Strict" preset');
                            }}
                            danger
                        >
                            Strict (50/min, 5 login/min)
                        </Button>
                    </Space>
                </Card>

                <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined style={{ marginRight: 4 }} />} loading={isSubmitting} size="large">
                        Save Rate Limiting Settings
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};
